import type { AsyncStatus } from 'lib-shared-legacy-types';
import { Action, handleActions } from 'redux-actions';


import { chatFlowActions } from '../actions/chatFlow';
import type {
  FlowChatAddFlowResponseT,
  FlowChatUpdateResponseT,
} from '../api/types';

import { chatFlowsGetAll } from './chatFlows';

export type ChatFlowStateT = {
  id?: null | string;
  isActive?: boolean;
  meta: {
    asyncStatus: AsyncStatus;
    hasUnSavedChanges?: boolean;
  };
  name: string;
  widget_id?: null | string;
};

type FlowChatHandleActionPayloadT =
  | FlowChatAddFlowResponseT
  | FlowChatUpdateResponseT
  | any;

export const {
  chatFlowAdd,
  chatFlowAddActionClicked,
  chatFlowAddAfterChatWidgetCreated,
  chatFlowAddFailed,
  chatFlowAddSucceeded,
  chatFlowClearState,
  chatFlowEdit,
  chatFlowEditFailed,
  chatFlowEditSucceeded,
  chatFlowGet,
  chatFlowGetFailed,
  chatFlowGetSucceeded,
} = chatFlowActions;

// Init state for the Chat Flow.
const initialState: ChatFlowStateT = {
  id: null,
  meta: {
    asyncStatus: 'succeeded',
    hasUnSavedChanges: false,
  },
  name: '',
  widget_id: null,
};

// Reducer
const chatFlow = handleActions<ChatFlowStateT, FlowChatHandleActionPayloadT>(
  {
    [chatFlowsGetAll.toString()]: (): ChatFlowStateT => {
      return initialState;
    },

    [chatFlowAdd.toString()]: (state: ChatFlowStateT): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [chatFlowAddSucceeded.toString()]: (
      state: ChatFlowStateT,
    ): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'succeeded' } };
    },

    [chatFlowAddFailed.toString()]: (state: ChatFlowStateT): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [chatFlowAddAfterChatWidgetCreated.toString()]: (
      state: ChatFlowStateT,
    ): ChatFlowStateT => {
      return { ...state };
    },

    [chatFlowEdit.toString()]: (state: ChatFlowStateT): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [chatFlowEditSucceeded.toString()]: (
      state: ChatFlowStateT,
    ): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'succeeded' } };
    },

    [chatFlowEditFailed.toString()]: (
      state: ChatFlowStateT,
    ): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [chatFlowGet.toString()]: (state: ChatFlowStateT): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'loading' } };
    },

    [chatFlowGetSucceeded.toString()]: (
      state: ChatFlowStateT,
      action: Action<FlowChatAddFlowResponseT>,
    ): ChatFlowStateT => {
      return {
        ...state,
        meta: { ...state.meta, asyncStatus: 'succeeded' },
        id: action.payload.data.id,
        name: action.payload.data.name,
        widget_id: action.payload.data.location,
        isActive: action.payload.data.active,
      };
    },

    ['CHAT_UPDATE_WIDGET_SUCCEEDED']: (
      state: ChatFlowStateT,
      action: any, // FlowChatUpdateResponseT
    ): ChatFlowStateT => {
      return { ...state, name: action.widget.updates.name };
    },

    [chatFlowGetFailed.toString()]: (state: ChatFlowStateT): ChatFlowStateT => {
      return { ...state, meta: { ...state.meta, asyncStatus: 'failed' } };
    },

    [chatFlowClearState.toString()]: (
      state: ChatFlowStateT,
    ): ChatFlowStateT => {
      return initialState;
    },
  },
  initialState,
);

export default chatFlow;
